import { format, parseISO, differenceInDays, isValid } from "date-fns";
import { formatCurrency } from "@/utils/currency";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Edit, Trash2, FileText, Receipt } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Helper function to safely parse and format dates
const safeParseDate = (dateString) => {
  if (!dateString) return null;
  try {
    const parsed = parseISO(dateString);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const safeFormatDate = (dateString, formatStr = "MMM dd, yyyy") => {
  const parsed = safeParseDate(dateString);
  return parsed ? format(parsed, formatStr) : "N/A";
};

export default function OrderList({ orders, workflows = { sublimation: [], embroidery: [] }, onEdit, onDelete, onStatusUpdate }) {
  const navigate = useNavigate();

  const getWorkflowForOrder = (order) => {
    // Use current workflow from settings based on printing method
    const currentWorkflow = order.printing_method === 'Sublimation' 
      ? (workflows?.sublimation || [])
      : (workflows?.embroidery || []);
    
    // If settings workflow is available, use it; otherwise fallback to order's saved workflow
    return currentWorkflow.length > 0 ? currentWorkflow : order.workflow;
  };

  const getStatusColor = (status) => {
    if (status === "Delivered") return "bg-green-100 text-green-800";
    if (status === "Design" || status === "Approved") return "bg-blue-100 text-blue-800";
    return "bg-amber-100 text-amber-800";
  };

  const calculateProgress = (workflow, currentStatus) => {
    const currentIndex = workflow.indexOf(currentStatus);
    return ((currentIndex + 1) / workflow.length) * 100;
  };

  const getDaysUntilDeadline = (deadline) => {
    const parsed = safeParseDate(deadline);
    if (!parsed) return 0;
    return differenceInDays(parsed, new Date());
  };

  const getDeadlineColor = (daysRemaining) => {
    if (daysRemaining < 0) return "text-red-600 font-semibold";
    if (daysRemaining <= 3) return "text-accent font-semibold";
    return "text-muted-foreground";
  };

  if (orders.length === 0) {
    return null; // Return null so parent component can show its own empty state
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-heading font-bold tracking-tight" data-testid="order-list-title">
        Orders ({orders.length})
      </h2>
      {orders.map((order) => {
        const daysRemaining = getDaysUntilDeadline(order.deadline);
        const currentWorkflow = getWorkflowForOrder(order);
        const progress = calculateProgress(currentWorkflow, order.current_status);

        return (
          <div
            key={order.id}
            className={`order-card bg-card border-2 rounded-md p-6 ${
              order.printing_method === 'Sublimation' 
                ? 'border-blue-300 bg-blue-50/30' 
                : 'border-purple-300 bg-purple-50/30'
            }`}
            data-testid={`order-card-${order.id}`}
          >
            <div className="flex flex-col md:flex-row gap-6">
              {/* Thumbnail Photo */}
              {(order.thumbnail || order.has_photos) && (
                <div className="flex-shrink-0">
                  {order.thumbnail ? (
                    <div className="relative">
                      <img
                        src={order.thumbnail}
                        alt={order.product_type}
                        className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-md border border-border"
                        loading="lazy"
                        data-testid="order-thumbnail"
                      />
                      {order.photo_count > 1 && (
                        <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                          +{order.photo_count - 1}
                        </span>
                      )}
                    </div>
                  ) : order.has_photos ? (
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-secondary rounded-md flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">Photo</span>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Order Details */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-heading font-bold text-foreground mb-1" data-testid="order-client-name">
                      {order.client_name}
                    </h3>
                    <p className="text-sm text-muted-foreground" data-testid="order-product-type">
                      {order.product_type} • 
                      <span className={`font-semibold ml-1 ${
                        order.printing_method === 'Sublimation' ? 'text-blue-600' : 'text-purple-600'
                      }`}>
                        {order.printing_method}
                      </span>
                    </p>
                  </div>
                  <div className="mt-2 md:mt-0">
                    <span
                      className={`status-badge inline-block px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${getStatusColor(
                        order.current_status
                      )}`}
                      data-testid="order-status-badge"
                    >
                      {order.current_status}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Deadline</p>
                    <p className={`font-medium ${getDeadlineColor(daysRemaining)}`} data-testid="order-deadline">
                      {safeFormatDate(order.deadline)}
                      <span className="block text-xs">
                        {daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` : `${daysRemaining} days left`}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Quantity</p>
                    <p className="font-medium" data-testid="order-quantity">{order.total_quantity} items</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Amount</p>
                    <p className="font-medium text-green-600" data-testid="order-amount">
                      {formatCurrency(order.total_amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Balance</p>
                    <p className={`font-medium ${order.balance > 0 ? 'text-amber-600' : 'text-green-600'}`} data-testid="order-balance">
                      {formatCurrency(order.balance || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Order ID</p>
                    <p className="font-mono text-xs truncate" data-testid="order-id">{order.id.slice(0, 8)}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Progress</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" data-testid="order-progress" />
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-3">
                  <Select
                    value={order.current_status}
                    onValueChange={(value) => onStatusUpdate(order.id, value)}
                  >
                    <SelectTrigger className="w-48" data-testid="status-update-dropdown">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currentWorkflow.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/print-job-order/${order.id}`)}
                    data-testid="print-job-order-button"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Job Order
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/print-invoice/${order.id}`)}
                    data-testid="print-invoice-button"
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    Invoice
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(order)}
                    data-testid="edit-order-button"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (window.confirm("Are you sure you want to delete this order?")) {
                        onDelete(order.id);
                      }
                    }}
                    className="text-red-600 hover:bg-red-50"
                    data-testid="delete-order-button"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
